/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useMediaQuery, Grid, Box, useTheme, Typography, Button } from "@mui/material";
import CustomRightImage from "@components/CustomRightImagev2";
import CreditOfferCard from "@components/CreditOfferCard";
import { RootState } from "@store/store";
import { useDispatch, useSelector } from "react-redux";
import { ApiError } from "@interfaces/index";
import { setModalErrorVisible } from "@store/slices/appSlice";
import * as Sentry from "@sentry/react";
import { setLoanStatus } from "@store/slices/registerSlice";
import { usePostValidateNextStageMutation } from "@api/applications";
import { useNavigate } from "react-router-dom";
import { STATUS_CATALOG, TAX_SYSTEM_TYPE } from "@helpers/constantes";
import { useLazyGetOffersQuery } from "@api/marketPlaceApis";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { LoanApplicationOfferResponse } from "@interfaces/MarketPlace";

const STATUS_OFFER_ACCEPTED_COMPANY = "CAOAC";
const URL_OFFER_ACCEPTED_COMPANY = "/producto/creditosimple/oferta_aceptada_company";

const OfertaSeleccionada: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const company = useSelector((state: RootState) => state.register.company);
  const offer = useSelector((state: RootState) => state.marketPalce.offers);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getOffersMarket] = useLazyGetOffersQuery();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [offerSelected, setOfferSelected] = useState<LoanApplicationOfferResponse | null>(null);
  const [hasOffer, setHasOffer] = useState<boolean>(false);

  const handleSelectedOffer = async () => {
    try {
      const { check_list, informacion_documentos_pfae } = STATUS_CATALOG;

      if (offerSelected?.financial_company.is_partner) {
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: STATUS_OFFER_ACCEPTED_COMPANY,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        navigate(URL_OFFER_ACCEPTED_COMPANY);
      } else {
        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: check_list.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        dispatch(setLoanStatus(check_list.code));

        if (application.tax_system_type == TAX_SYSTEM_TYPE.PM.code) {
          navigate(check_list.url);
        } else {
          navigate(informacion_documentos_pfae.url);
        }
      }
    } catch (error) {
      const api_error = error as ApiError;
      Sentry.captureException(error, {
        extra: {
          component: "OfertaSeleccionada-initial_fetch",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    }
  };

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const responseOffers = await getOffersMarket({ id_sol: application.id }).unwrap();

        const selected: LoanApplicationOfferResponse[] = responseOffers.filter((of) => of?.accept);

        if (selected.length > 0) {
          setOfferSelected(selected[0]);
          dispatch(setSelectedOffer(selected[0]));
          setHasOffer(true);
        } else {
          setHasOffer(false);
        }

        dispatch(setSelectedOffer(selected[0]));
      } catch (error) {
        const api_error = error as ApiError;
        Sentry.captureException(error, {
          extra: {
            component: "SolicitudCreditoPart1-initial_fetch",
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8} mt={4}>
        <Typography
          sx={{ mb: 3 }}
          variant={isMobileOrTablet ? "h4" : "h3"}
          fontWeight={600}
          color={theme?.palette?.primary?.dark}
          textAlign={"center"}
        >
          ¡Tenemos buenas noticias! <br />
          {company.name}
        </Typography>

        <Box mr={3} ml={3}>
          <Typography
            sx={{ mb: 3 }}
            variant={"body1"}
            fontWeight={500}
            color={theme?.palette?.primary?.main}
            textAlign={"center"}
          >
            Notificamos a la institución que aceptaste la oferta.
            <br />
            <br />
            Continua para completar tu expediente digital y obtener tu financiamiento.
          </Typography>
        </Box>

        <Box
          mt={6}
          sx={{
            boxShadow: "8px 4px 20px 0px #58585840",
            borderRadius: 10,
            p: "10px 0", // paddingTop y paddingBottom juntos
            maxWidth: 500,
            margin: "0 auto", // centra horizontalmente
            display: "flex",
            justifyContent: "center",
          }}
        >
          {hasOffer ? (
            <>
              <CreditOfferCard key={offer?.id} offer={offer} offers_length={0} showButton={false} />
            </>
          ) : (
            <Typography variant="body1">No se encontró una oferta seleccionada.</Typography>
          )}
        </Box>

        <Box mr={3} ml={3} mt={3}>
          <Typography
            sx={{ mb: 3 }}
            variant={"body1"}
            fontWeight={300}
            color={theme?.palette?.primary?.dark}
            textAlign={"center"}
          >
            ¡Despreocúpate! <br />
            Puedes salir de esta pantalla, tan pronto se valide tu información podrás proceder a cargar tu expediente.
          </Typography>
        </Box>
        <Box display="none" justifyContent="center" mt={isMobileOrTablet ? 2 : 4} mb={1}>
          <Button variant="default" onClick={handleSelectedOffer} sx={{ width: "130px" }} disabled={isLoading}>
            Continuar
          </Button>
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default OfertaSeleccionada;
